// The talk lab's page (`?scene=talk`, `&talk=open` for open talk; capability round 1, IMP-0044): the units' game from lab.json (game.ts), drawn.
// The stage is the lab's own: a lawn, a stall, a gate post and a capsule for each actor, the A1 pen hidden (as the
// camera lab does). Each frame the scene player's view places the actors and the camera while a scene plays, else the
// actors stand where the scenes left them under a fixed overview; the units' own box and overlay draw the talk and the
// scene. Two buttons say what the player did (talk to Mira; reach the gate). Nothing here decides anything about a
// talk or a scene: that is the units' and the content's.
import * as THREE from "three";
import type { Runtime } from "@rpg/runtime";
import { mountConversationBox } from "@rpg-unit/conversation";
import { mountSceneOverlay } from "@rpg-unit/scene-player";
import { createTalkLab, TALK_LAB_ACTIONS } from "./game";

export function mountTalkLab(o: { runtime: Runtime; scene: THREE.Scene; camera: THREE.PerspectiveCamera; search?: string }) {
  const q = new URLSearchParams(o.search ?? location.search);
  if (q.get("scene") !== "talk") return null;
  const { runtime, scene, camera } = o;
  // `&talk=open`: Mira talks openly (typed or suggested lines)
  const lab = createTalkLab({ talk: q.get("talk") === "open" ? "open" : "static" });
  const dispatch = (a: string, p?: unknown): void => lab.game.dispatch(a, p);

  // the stage
  const pen = scene.getObjectByName("sim-test-scene");
  if (pen) pen.visible = false;
  scene.background = new THREE.Color("#cfe3c0");
  const group = new THREE.Group();
  group.name = "talk-lab";
  scene.add(group);
  group.add(new THREE.HemisphereLight("#fff6e0", "#5d7a4a", 1.2));
  const sun = new THREE.DirectionalLight("#fff1d0", 1.4);
  sun.position.set(8, 14, -6);
  group.add(sun);
  const lawn = new THREE.Mesh(new THREE.CircleGeometry(14, 40), new THREE.MeshLambertMaterial({ color: "#8fb36b" }));
  lawn.rotation.x = -Math.PI / 2;
  group.add(lawn);
  const stall = new THREE.Mesh(new THREE.BoxGeometry(2.4, 1, 0.8), new THREE.MeshLambertMaterial({ color: "#b5835a", flatShading: true }));
  stall.position.set(0, 0.5, 1);
  group.add(stall);
  const post = new THREE.Mesh(new THREE.BoxGeometry(0.3, 1.6, 0.3), new THREE.MeshLambertMaterial({ color: "#6b4e34", flatShading: true }));
  post.position.set(5.8, 0.8, 6.6);
  group.add(post);
  const actors = new Map<string, THREE.Group>();
  for (const [id, colour] of [
    ["mira", "#c85a7a"],
    ["hero", "#3f6fb5"],
  ] as const) {
    const g = new THREE.Group();
    const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.3, 0.9, 4, 10), new THREE.MeshLambertMaterial({ color: colour, flatShading: true }));
    body.position.y = 0.75;
    const nose = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.12, 0.25), new THREE.MeshLambertMaterial({ color: "#222" }));
    nose.position.set(0, 1.3, 0.3);
    g.add(body, nose);
    group.add(g);
    actors.set(id, g);
  }

  // the units' box and overlay, and the lab's two buttons
  const box = mountConversationBox({ feature: lab.talk, dispatch, freeText: true });
  const overlay = mountSceneOverlay({ player: lab.scenes, dispatch, skipLabel: "Skip" });
  const bar = document.createElement("div");
  bar.style.cssText = "position:fixed;left:16px;top:14px;display:flex;gap:8px;z-index:20";
  for (const [label, action] of [
    ["Talk to Mira", TALK_LAB_ACTIONS.talk],
    ["Walk to the gate", TALK_LAB_ACTIONS.gate],
  ] as const) {
    const b = document.createElement("button");
    b.textContent = label;
    b.style.cssText = "font:14px system-ui,sans-serif;padding:6px 12px;border-radius:8px;border:1px solid #0003;background:#fffc";
    b.addEventListener("click", () => dispatch(action));
    bar.append(b);
  }
  document.body.append(bar);

  runtime.on("sim", (c) => lab.game.tick(c.dt), { name: "talk lab: game" });
  runtime.on(
    "late",
    () => {
      const v = lab.scenes.view();
      for (const [id, g] of actors) {
        const at = v?.actors[id] ?? lab.scenes.placed(id);
        if (!at) continue;
        g.position.set(at.pos[0], at.pos[1], at.pos[2]);
        g.rotation.y = at.yaw;
      }
      if (v?.camera) {
        camera.position.set(v.camera.position.x, v.camera.position.y, v.camera.position.z);
        camera.lookAt(v.camera.target.x, v.camera.target.y, v.camera.target.z);
        camera.fov = v.camera.fov;
      } else {
        camera.position.set(2, 9, -9);
        camera.lookAt(2, 0, 2);
        camera.fov = 50;
      }
      camera.updateProjectionMatrix();
      bar.style.visibility = v ? "hidden" : "visible";
      box.update();
      overlay.update();
    },
    { name: "talk lab: view" },
  );

  return {
    /** The lab's state for a spec or a shot. */
    state: () => ({ talk: lab.talk.view(), scene: lab.scenes.view(), flags: { ...lab.talk.flags(), ...lab.scenes.flags() }, seen: lab.scenes.seen(), minutes: lab.talk.minutes(), paused: lab.game.paused() }),
    /** Dispatch a lab or unit action (`lab/talk`, `lab/gate`, `conversation/advance`, `scene/skip`, …). */
    dispatch,
  };
}
